// Core imports
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Button, Form, Input, Popover, Space } from 'antd';
import { SearchOutlined, CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';

// Misc imports
import useDebouncedCallback from '../../utils/hooks/useDebounce';
import { INTERACTION_LOGS_SEARCH_BY_TYPES } from '../../constants/main';

const SearchOptionsDropdown = memo(({ searchByType, onOptionClick, t }) => (
  <Space direction="vertical" className="w-auto" style={{ minWidth: '200px' }}>
    {Object.values(INTERACTION_LOGS_SEARCH_BY_TYPES).map(type => (
      <Button
        key={type.key}
        type="text"
        block
        className={classNames('fw-400 mb-1 text-left', {
          'bg-body': searchByType === type.key
        })}
        onClick={() => onOptionClick(type.key)}
      >
        {t(type.placeHolderText)}
      </Button>
    ))}
  </Space>
));

SearchOptionsDropdown.displayName = 'SearchOptionsDropdown';

SearchOptionsDropdown.propTypes = {
  searchByType: PropTypes.string.isRequired,
  onOptionClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
};

const InteractionLogsSearchByInput = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();

  const [searchByType, setSearchByType] = useState(
    () => searchParams.get('searchBy') || INTERACTION_LOGS_SEARCH_BY_TYPES.SESSION_ID.key
  );
  const [isSearchTypePopoverOpen, setIsSearchTypePopoverOpen] = useState(false);

  useEffect(() => {
    const termFromUrl = searchParams.get('searchQuery') ? decodeURIComponent(searchParams.get('searchQuery')) : '';
    const typeFromUrl = searchParams.get('searchBy') || INTERACTION_LOGS_SEARCH_BY_TYPES.SESSION_ID.key;
    form.setFieldValue('searchQuery', termFromUrl);
    setSearchByType(typeFromUrl);
  }, [searchParams, form]);

  const updateUrlParams = useCallback((typeOverride) => {
    const term = form.getFieldValue('searchQuery') || '';
    const type = typeOverride ?? searchByType;
    const newUrlParams = new URLSearchParams(searchParams);
    if (term) {
      newUrlParams.set('searchQuery', encodeURIComponent(term));
    } else {
      newUrlParams.delete('searchQuery');
    }
    newUrlParams.set('searchBy', type);
    newUrlParams.set('page', '1');
    navigate({ search: newUrlParams.toString() }, { replace: true });
  }, [searchParams, navigate, form, searchByType]);

  const debouncedUpdateUrl = useDebouncedCallback(updateUrlParams, 700);

  const handleSearchOptionsOnClick = useCallback((newTypeKey) => {
    setIsSearchTypePopoverOpen(false);
    setSearchByType(newTypeKey);
    updateUrlParams(newTypeKey);
  }, [updateUrlParams]);

  const currentPlaceholder = (() => {
    const foundType = Object.values(INTERACTION_LOGS_SEARCH_BY_TYPES).find(type => type.key === searchByType);
    return foundType ? t(foundType.placeHolderText) : t('Search');
  })();

  return (
    <Form
      form={form}
      initialValues={{
        searchQuery: searchParams.get('searchQuery') ? decodeURIComponent(searchParams.get('searchQuery')) : ''
      }}
    >
      <Form.Item name="searchQuery" className="mb-0">
        <Input
          size="large"
          placeholder={currentPlaceholder}
          allowClear
          prefix={<SearchOutlined className="text-muted" />}
          onChange={() => debouncedUpdateUrl()}
          addonAfter={
            <Popover
              open={isSearchTypePopoverOpen}
              onOpenChange={setIsSearchTypePopoverOpen}
              placement="bottomRight"
              trigger="click"
              content={
                <SearchOptionsDropdown
                  searchByType={searchByType}
                  onOptionClick={handleSearchOptionsOnClick}
                  t={t}
                />
              }
            >
              <Button
                className="text-secondary bg-none shadow-none border-none"
                type="text"
                icon={isSearchTypePopoverOpen ? <CaretUpOutlined /> : <CaretDownOutlined />}
              />
            </Popover>
          }
        />
      </Form.Item>
    </Form>
  );
});

InteractionLogsSearchByInput.displayName = 'InteractionLogsSearchByInput';

export default InteractionLogsSearchByInput;
